import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, Check, ClipboardList, Gift, Share2 } from "lucide-react";
import StepHeader from "@/components/StepHeader";
import { DemoOfferModal } from "@/components/AdvisorForms";
import { FeatureHotspots } from "@/components/FeatureHotspots";
import { readAdvisorState } from "@/data/polyFitData";
import { isPreviewMode, useAdvisorContent } from "@/hooks";
import { trackAdvisorEvent } from "@/utils/analytics";

export default function FitRecommendationPage() {
  const navigate = useNavigate();
  const { products, questions, copy } = useAdvisorContent();
  const [advisorState] = useState(() => { const state = readAdvisorState(); const previewProduct = isPreviewMode() ? new URLSearchParams(window.location.search).get("product") : ""; return previewProduct ? { ...state, recommendationId: previewProduct, path: ["workplace", "desks-rooms-mobility"], answers: { workplace: "desks-rooms", "desks-rooms-mobility": "roam-floor" } } : state; });
  const [demoOpen, setDemoOpen] = useState(false);
  const [visualTab, setVisualTab] = useState("product");
  const product = products[advisorState.recommendationId];
  const hasHotspots = Boolean(product?.hotspots?.points?.length);
  const showHotspots = hasHotspots && visualTab === "hotspots";
  const priorities = [...new Set((advisorState.path || []).flatMap((qid) => { const picked = [].concat(advisorState.answers?.[qid] || []); return (questions[qid]?.options || []).filter((o) => picked.includes(o.id)).flatMap((o) => o.priorities || []); }))];

  useEffect(() => {
    if (!product) {
      navigate("/question/workplace", { replace: true });
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" });
    trackAdvisorEvent("recommendation_viewed", { sessionId: advisorState.sessionId, result_product_id: product.id, completed_steps: advisorState.completedSteps });
  }, [advisorState.completedSteps, advisorState.sessionId, navigate, product]);

  if (!product) return null;

  const openDemo = () => {
    setDemoOpen(true);
    trackAdvisorEvent("lead_capture_opened", { sessionId: advisorState.sessionId, result_product_id: product.id });
  };

  const compare = () => trackAdvisorEvent("compare_all_models_clicked", { sessionId: advisorState.sessionId, result_product_id: product.id });

  const share = async () => {
    const text = `My HP Poly fit is ${product.brandName}`;
    try {
      if (navigator.share) await navigator.share({ title: "Find Your Poly Fit", text, url: window.location.href });
      else await navigator.clipboard.writeText(`${text} - ${window.location.href}`);
    } catch { /* user cancelled share */ }
  };

  return (
    <main className="advisor-page recommendation-page" data-testid="recommendation-page">
      <StepHeader step={3} compact progressLabel={`${advisorState.completedSteps || 3}-step path completed`} />
      <section className="recommendation-stage reveal-in" data-testid="recommendation-shell">
        <header className="recommendation-head" data-testid="recommendation-copy">
          <p className="step-kicker" data-testid="recommendation-label">{copy.result.kicker}</p>
          <p className="product-series" data-testid="recommendation-product-series">{product.series}</p>
          <h1 data-testid="recommendation-model-name">{product.brandName}</h1>
          <p className="recommendation-lede" data-testid="recommendation-best-for">{product.tagline}</p>
        </header>
        {hasHotspots && (
          <div className="visual-tabs" role="tablist" data-testid="recommendation-visual-tabs">
            <button type="button" role="tab" className={!showHotspots ? "active" : ""} aria-selected={!showHotspots} onClick={() => setVisualTab("product")} data-testid="recommendation-tab-product">{copy.result.visual_tab_product}</button>
            <button type="button" role="tab" className={showHotspots ? "active" : ""} aria-selected={showHotspots} onClick={() => setVisualTab("hotspots")} data-testid="recommendation-tab-features">{copy.result.visual_tab_features}</button>
          </div>
        )}
        {showHotspots ? (
          <FeatureHotspots hotspots={product.hotspots} productId={product.id} productName={product.brandName} />
        ) : (
          <div className="recommendation-visual" data-testid="recommendation-visual">
            <img src={product.imageUrl} alt={product.brandName} data-testid="recommendation-product-image" />
          </div>
        )}
        {priorities.length > 0 && (
          <div className="fit-reasons" data-testid="recommendation-priorities">
            <h2 data-testid="recommendation-why-fits">{copy.result.why_heading}</h2>
            <div className="priority-chips" data-testid="recommendation-priority-chips">
              {priorities.map((item) => <span key={item} data-testid={`recommendation-priority-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}><Check size={14} /> {item}</span>)}
            </div>
          </div>
        )}
        {!showHotspots && (
          <ul className="feature-grid" data-testid="recommendation-feature-list">
            {product.features.map((feature) => <li key={feature} data-testid={`recommendation-feature-${feature.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>{feature}</li>)}
          </ul>
        )}
        <p className="also-consider" data-testid="recommendation-also-consider">{copy.result.also_consider_label} {product.alsoConsider}</p>
        <div className="route-summary" data-testid="recommendation-routing-summary"><ClipboardList size={14} /> {(advisorState.path || []).join(" → ") || "fit-check"}</div>
        <div className="recommendation-actions" data-testid="recommendation-action-row">
          <button className="primary-cta pill-cta" type="button" onClick={openDemo} data-testid="recommendation-demo-offer-button"><Gift size={17} /> {copy.result.demo_cta}</button>
          <button className="ghost-btn pill-cta" type="button" onClick={share} data-testid="recommendation-share-social-button"><Share2 size={16} /> {copy.result.share_cta}</button>
          <Link className="ghost-btn pill-cta" to="/compare" onClick={compare} data-testid="recommendation-comparison-guide-button"><BarChart3 size={16} /> {copy.result.compare_cta}</Link>
        </div>
      </section>
      <DemoOfferModal open={demoOpen} onClose={() => setDemoOpen(false)} product={product} advisorState={advisorState} />
    </main>
  );
}